import { z } from "zod";
import { addMonthsUtc } from "./month-arithmetic";

// TES-937 — who may switch to "Ella Underhåll". Step 4 of TES-886: a pure
// function with no automatic effect.
//
// THE RULE IS NOT THE ONE IN TES-886. TES-886 quotes the subscription
// contract's ELIGIBILITY block:
//
//   completed_months_in_ella_uppstart >= 12
//   AND clinical_phase = WEIGHT_STABILISATION
//   AND patient_explicitly_requests_plan_change
//
// The same Drive document (1aBik1S3HxXG1cmSnbCnK4GYZ__XRSw2NmfMdHlE3t6E) now
// ends with "ÖVERORDNAD RÄTTELSE — NAMN OCH BEHÖRIGHET — 2026-09-23", which
// replaces that block (Emelie's Ö-221 answer, TES-907):
//
//   "Detta avsnitt ersätter äldre krav på tolv månader just hos Ella, krav på"
//   "WEIGHT_STABILISATION och användning av ”Uppföljning” som abonnemangsnamn."
//   "Ella Underhåll får väljas när patienten har minst tolv månaders"
//   "dokumenterad medicinsk viktbehandling totalt. Verifierad behandling hos"
//   "tidigare vårdgivare får räknas. Behörigheten är oberoende av"
//   "clinical_phase"
//   "Ella Underhåll väljs uttryckligen av patienten, är aldrig förvalt"
//
// So what is encoded here:
//   1. twelve months of documented medical weight treatment IN TOTAL, counted
//      from the earliest documented start: a verified start at an earlier
//      provider, or the start of treatment with Ella, whichever is earlier;
//   2. the patient's explicit request.
// The clinical phase is deliberately NOT an input: a function that took it
// would invite a caller to pass it, and a reader to assume it matters.
//
// How the months are counted:
//   - from the start of TREATMENT, not of the subscription. The rule is about
//     medical treatment, so a free or trial subscription month is irrelevant
//     either way; it neither adds nor subtracts.
//   - calendar months via `addMonthsUtc` (the billing term's arithmetic), on
//     UTC calendar days: eligible from the day `start + 12 months` is reached.
//     A start on 31 May or 29 February clamps to the last day of the target
//     month, as Stripe does.
//   - a pause does not subtract. Same convention as the transfer patient's
//     rule (transfer-plan-choice.ts, Emelie point 5): "A pause is medically
//     relevant but never makes her ineligible by itself." The canonical
//     document says "totalt" and nothing about pauses.
//
// TES-1073 (Ö-297, Ö-298; Emelie 2026-10-02, recorded in a TES-475 comment):
// the earlier start now carries a PRECISION and a SOURCE, and an uncertain
// date must never qualify early:
//   - exact: counted from the date;
//   - month: counted from the LAST day of that month;
//   - year: counted from the LAST day of that year;
//   - estimated: counts only once the doctor has explicitly confirmed that it
//     shows at least twelve full months, and then from the date as given.
// An unconfirmed estimate is ignored, not rounded: there is no conservative
// reading of a guess. The server refuses to issue an "Ella Underhåll" link
// unless `underhallTreatmentReasons` is empty (Ö-297's hard block, in
// apps/api subscription-link.service.ts).

// ---- the documented earlier start (TES-1073) --------------------------------

export const TREATMENT_START_DATE_PRECISIONS = [
  "exact",
  "month",
  "year",
  "estimated",
] as const;
export const treatmentStartDatePrecisionSchema = z.enum(
  TREATMENT_START_DATE_PRECISIONS,
);
export type TreatmentStartDatePrecision = z.infer<
  typeof treatmentStartDatePrecisionSchema
>;

export const TREATMENT_START_SOURCES = [
  "patient_reported",
  "prescription_document",
  "medical_record",
  "doctor_assessed",
] as const;
export const treatmentStartSourceSchema = z.enum(TREATMENT_START_SOURCES);
export type TreatmentStartSource = z.infer<typeof treatmentStartSourceSchema>;

export type DocumentedEarlierStart = {
  /** The recorded date at UTC midnight; month/year: the first of the period. */
  startedAt: Date;
  precision: TreatmentStartDatePrecision;
  /**
   * For `estimated` only: when the doctor confirmed that the estimate shows
   * at least twelve full months. Null means not confirmed, and then the
   * estimate does not count at all.
   */
  estimateConfirmedAt: Date | null;
};

export const UNDERHALL_INELIGIBLE_REASONS = [
  // No documented treatment start at all: neither an earlier start that
  // counts nor a start with Ella.
  "treatment_start_missing",
  // TES-1073 — the earlier start is an estimate the doctor has not confirmed
  // as showing twelve full months, so it is not counted.
  "estimate_not_confirmed",
  // The documented start, read conservatively, is less than twelve months
  // back.
  "less_than_twelve_months",
  // She has not explicitly asked for it. Never inferred: "aldrig förvalt".
  "not_requested_by_patient",
] as const;
export type UnderhallIneligibleReason =
  (typeof UNDERHALL_INELIGIBLE_REASONS)[number];

export const underhallEligibilitySchema = z.object({
  status: z.enum(["eligible", "not_eligible"]),
  reasons: z.array(z.enum(UNDERHALL_INELIGIBLE_REASONS)),
});
export type UnderhallEligibility = z.infer<typeof underhallEligibilitySchema>;

export type UnderhallEligibilityInput = {
  /**
   * The documented earlier start: the doctor's registration (TES-1073), or
   * else the start he verified when approving a prescription transfer
   * (`Treatment.currentTreatmentStartedAt`, TES-734). Null when neither
   * exists.
   */
  earlierStart: DocumentedEarlierStart | null;
  /** When treatment with Ella began (the first cycle's `activatedAt`). */
  ellaStartedAt: Date | null;
  /** Her explicit request to change plan. */
  patientRequested: boolean;
};

type TreatmentInput = Pick<
  UnderhallEligibilityInput,
  "earlierStart" | "ellaStartedAt"
>;

function valid(d: Date | null | undefined): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function utcDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * The UTC day the twelve months are counted from, read conservatively, or
 * null when the start does not count (an unconfirmed estimate).
 */
export function countedFrom(start: DocumentedEarlierStart): Date | null {
  if (!valid(start.startedAt)) return null;
  const y = start.startedAt.getUTCFullYear();
  const m = start.startedAt.getUTCMonth();
  switch (start.precision) {
    case "exact":
      return new Date(utcDay(start.startedAt));
    case "month":
      // Day 0 of the next month is the last day of this one.
      return new Date(Date.UTC(y, m + 1, 0));
    case "year":
      return new Date(Date.UTC(y, 11, 31));
    case "estimated":
      return valid(start.estimateConfirmedAt)
        ? new Date(utcDay(start.startedAt))
        : null;
    default:
      // An unknown precision is not a reason to count anything.
      return null;
  }
}

/**
 * The first UTC day on which the documented treatment reaches twelve months,
 * or null when no start counts. The earlier of the two starts decides.
 */
export function underhallEligibleFrom(input: TreatmentInput): Date | null {
  const starts = [
    input.earlierStart ? countedFrom(input.earlierStart) : null,
    valid(input.ellaStartedAt) ? new Date(utcDay(input.ellaStartedAt)) : null,
  ].filter(valid);
  if (starts.length === 0) return null;
  const from = new Date(Math.min(...starts.map((d) => d.getTime())));
  return new Date(utcDay(addMonthsUtc(from, 12)));
}

/**
 * The treatment half of the rule: what stands between her and twelve
 * documented months at `at`. Empty means the condition is met. This is what
 * the server's hard block reads (Ö-297); her request is a separate condition,
 * checked where she makes it.
 */
export function underhallTreatmentReasons(
  input: TreatmentInput,
  at: Date,
): UnderhallIneligibleReason[] {
  const unconfirmedEstimate =
    input.earlierStart !== null &&
    input.earlierStart.precision === "estimated" &&
    countedFrom(input.earlierStart) === null;
  const from = underhallEligibleFrom(input);
  if (from === null) {
    return [
      unconfirmedEstimate
        ? "estimate_not_confirmed"
        : "treatment_start_missing",
    ];
  }
  if (from.getTime() > utcDay(at)) {
    return unconfirmedEstimate
      ? ["estimate_not_confirmed", "less_than_twelve_months"]
      : ["less_than_twelve_months"];
  }
  return [];
}

/**
 * Whether the patient may switch to "Ella Underhåll" at `at`.
 *
 * Eligibility is not an offer and changes nothing:
 * "Eligibility är inte ett erbjudande och ändrar inget automatiskt."
 * Every unmet condition is listed,
 * so a caller can say what is missing rather than only that something is.
 */
export function underhallEligibility(
  input: UnderhallEligibilityInput,
  at: Date,
): UnderhallEligibility {
  const reasons = underhallTreatmentReasons(input, at);
  if (input.patientRequested !== true) {
    reasons.push("not_requested_by_patient");
  }
  return reasons.length === 0
    ? { status: "eligible", reasons: [] }
    : { status: "not_eligible", reasons };
}
